import { useCallback, useMemo, useState } from 'react';

interface SelectableRow {
  proposalId: number;
  proposedAmount: number;
}

interface SelectionState {
  /** Every row the list matches, minus `excluded` ("select all matching"). */
  allMatching: boolean;
  /** Picked rows (or, with allMatching, the unticked ones), by id → amount. */
  picked: Map<number, number>;
}

const EMPTY: SelectionState = { allMatching: false, picked: new Map() };

/**
 * Row selection across pages, with a Gmail-style "select all matching".
 * It clears whenever `resetKey` changes (tab, filters).
 */
export function usePcmpRowSelection(resetKey: string) {
  const [state, setState] = useState<SelectionState>(EMPTY);
  const [key, setKey] = useState(resetKey);
  if (key !== resetKey) {
    setKey(resetKey);
    setState(EMPTY);
  }

  const isSelected = useCallback(
    (id: number) => (state.allMatching ? !state.picked.has(id) : state.picked.has(id)),
    [state],
  );

  const setRows = useCallback((rows: SelectableRow[], selected: boolean) => {
    setState((prev) => {
      const picked = new Map(prev.picked);
      // With allMatching, `picked` holds the exclusions: the meaning flips.
      const add = prev.allMatching ? !selected : selected;
      for (const row of rows) {
        if (add) picked.set(row.proposalId, row.proposedAmount);
        else picked.delete(row.proposalId);
      }
      return { ...prev, picked };
    });
  }, []);

  const toggle = useCallback(
    (row: SelectableRow) => setRows([row], !isSelected(row.proposalId)),
    [isSelected, setRows],
  );

  const selectAllMatching = useCallback(
    () => setState({ allMatching: true, picked: new Map() }),
    [],
  );
  const clear = useCallback(() => setState(EMPTY), []);

  const pickedAmount = useMemo(
    () => [...state.picked.values()].reduce((sum, amount) => sum + amount, 0),
    [state.picked],
  );

  /** Count and amount, given the list's total and amount for "all matching". */
  const summarize = useCallback(
    (total: number, totalAmount: number) =>
      state.allMatching
        ? { count: total - state.picked.size, amount: totalAmount - pickedAmount }
        : { count: state.picked.size, amount: pickedAmount },
    [state, pickedAmount],
  );

  return {
    allMatching: state.allMatching,
    pickedIds: [...state.picked.keys()],
    isSelected,
    setRows,
    toggle,
    selectAllMatching,
    clear,
    summarize,
  };
}

export type PcmpRowSelection = ReturnType<typeof usePcmpRowSelection>;
